import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import {
  setShowFreeSpinMaxWin,
  setShowTransitionDialog,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import {
  startOutroWinMusic,
  stopOutroWinMusic,
} from "../../../../Games/Shared/outroWinMusic";
import TickerTimeout from "../../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle, WIN_TEXT_SCALE } from "../../../../fontStyles";
import { formatToCustomLocale } from "../../../../utilities";
import attachTapToContinue from "../../../../utils/attachTapToContinue";
import store from "../../../../State/Store/store";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import anime from "animejs";

// const SPINE_NAME = "win_celebration";
// const SKIN_NAME = "max_win";
// Its own spine (animator, 2026-09-30): `max_win` — in / idle / out, default
// skin only, txt_slot_value / txt_slot_xbet_value. The tap slot/bone is a hand
// edit: `txt_slot_taptostart` (3, -856.5) -> `txt_slot_tap_to_continue`
// (0, -480.24), the Total Win position the table below lifts onto the shared
// tap line. win_celebration no longer carries a max_win skin.
const SPINE_NAME = "max_win";
const SKIN_NAME = null;

// ============================================================================
// Sizing — the WinAnimationSecondary baseline, as every Mad Labs popup takes
// it. All 1.0 until a device proves otherwise.
// ============================================================================
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the word at the SAME screen position, so opening two at
// once shows them perfectly aligned. Total win sets that position: its bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has.
//
// This spine's tap bone is at -480.24, so it needs -43.66 to reach the same line.
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// Natural size runs too large on a standard landscape phone.
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// Desktop takes the landscape-phone treatment: same trim, same tap-word lift.
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
// As the Total Win: the value sat glued to the tap line (2026-10-06).
const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.95, offset: 0.0, buttonYOffset: -43.66 };

const VALUE_SLOTS = ["txt_slot_value"];
// The current win_celebration export calls this `txt_slot_xbet_value`; Zeus
// called it `txt_slot_multiplier`. Tried in order — the three Pot Collection
// popups still ask only for the Zeus name and so draw no multiplier at all.
const MULTIPLIER_SLOTS = ["txt_slot_xbet_value", "txt_slot_multiplier"];
const BUTTON_SLOTS = ["txt_slot_tap_to_continue", "txt_slot_taptocontinue"];

// GDD §16.4 — Max Win has NO auto-close. Explicit user action only.
// Fallback only — input unlocks when the spine finishes revealing the tap
// word. See attachTapToContinue.revealMs.
// The run-up, matched to FreeSpinTotalWinController so the popup this one
// REPLACES is paced identically.
const COUNTER_DURATION_MS = 2000;
const SKIP_PROTECTION_MS = 1000;
const CLICK_DEBOUNCE_MS = 300;

/**
 * FreeSpinMaxWinController — the win cap, hit during free spins.
 *
 * The `max_win` skin of the shared `win_celebration` spine, run the same way
 * PotCollectionMaxWinDialogController runs it: fade the feature music out,
 * play `in` → looping `idle`, hold on the value, and wait for the player. It
 * is a SEPARATE component rather than a shared one so the free-spin version
 * can diverge without disturbing Zeus.
 *
 * Differences from the Pot Collection original, all deliberate:
 *   - the multiplier slot is a candidate list (see MULTIPLIER_SLOTS)
 *   - no coin shower: that is a Zeus FXManager effect
 *   - dismissal only closes the popup. What follows the free-spin feature is
 *     the round's business, not this popup's — see finishAnimation.
 */
class FreeSpinMaxWinController {
  // constructor(container, dimensionsContext, dispatch, symbolHeight, overlayRef, t) {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
    // The flag this popup lowers on close — the free-spins one by default;
    // Hold & Win passes setShowHoldAndWinMaxWin (its capped ending, 2026-10-01).
    closeAction = setShowFreeSpinMaxWin,
  ) {
    this.closeAction = closeAction;
    this.isHoldAndWin = closeAction !== setShowFreeSpinMaxWin;
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext?.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;
    this.inIdleTimeout = null;
    this._showRAF = null;

    this.counterText = null;
    this.multiplierText = null;

    // ── The counter, ported from FreeSpinTotalWinController ──
    //
    // This popup used to SET its value and stop. That was survivable while it
    // was unreachable; on a real capped round the player was shown the biggest
    // number the game can pay as a figure that was simply already there.
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;
    this._counterFinished = false;
    // Latch for _revealAfterCounter, so the xBet line and the tap word are
    // faded in exactly once per showing.
    this._revealedAfterCounter = false;
    this._revealPending = false;
    this._closing = false;
    this.buttonText = null;
    this._fitTapButton = null;

    this._totalWinValue = 0;
    this._betAmount = 0;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeSpine();
  }

  // ── Sizing ───────────────────────────────────────────────────────────────

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.spine.scale.set(scale);
    this.spine.x = 0;
    this.spine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

    this._fitTapButton?.(this.dimensions);
    this.applyButtonOffset();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDims = this.dimensionsContext?.dimensions;
    const newDims = dimensionsContext?.dimensions;

    const changed =
      !oldDims ||
      oldDims.width !== newDims?.width ||
      oldDims.height !== newDims?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;
    if (!changed) return;

    const deviceChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;

    if (deviceChanged && this.buttonText) this.updateButtonText();
    this.applyScaleAndCenter();
    // Both texts refit against the new screen width.
    //
    // The counter redraws at whatever it is CURRENTLY showing, not the total:
    // a resize or an orientation flip mid-count would otherwise snap the
    // number straight to the final figure and let the run-up carry on from
    // there. FreeSpinTotalWinController takes the same care.
    this.updateCounterDisplay(this.currentCounterValue ?? this._totalWinValue);
    this.updateMultiplierDisplay(this._totalWinValue);
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(`${SPINE_NAME}-skeleton`) ||
        !assets.has(`${SPINE_NAME}-atlas`)
      ) {
        console.warn("[FreeSpinMaxWin] Spine not loaded:", SPINE_NAME);
        return;
      }

      this.spine = Spine.from({
        skeleton: `${SPINE_NAME}-skeleton`,
        atlas: `${SPINE_NAME}-atlas`,
      });
      if (!this.spine?.skeleton || !this.spine?.state) return;

      this.container.addChild(this.spine);
      // Hidden by ALPHA, never by unmounting — that is what gives `out` frames.
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      this.spine.zIndex = 20;

      this.applySkin();
      this.applyScaleAndCenter();
      this.initialized = true;

      this.warmUpSpine();
      this.initializeCounter();
      this.initializeMultiplier();
      this.initializeButton();
    } catch (error) {
      console.warn("[FreeSpinMaxWin] Init failed:", error);
    }
  }

  applySkin() {
    // Its own spine now — one picture (default skin), nothing to set.
    if (!SKIN_NAME) return;
    const data = this.spine?.skeleton?.data;
    if (!data?.findSkin(SKIN_NAME)) {
      console.warn("[FreeSpinMaxWin] Missing skin:", SKIN_NAME);
      return;
    }
    this.spine.skeleton.setSkinByName(SKIN_NAME);
    // Slots only — setToSetupPose would reset BONES and snap the pose.
    this.spine.skeleton.setSlotsToSetupPose();
  }

  warmUpSpine() {
    if (!this.spine?.state) return;
    try {
      if (this.spine) this.spine.autoUpdate = true;
      this.spine.state.setAnimation(0, "in", false);
      this.spine.state.update(0.016);
      // Spine 4.2 REQUIRES an argument here.
      if (this.spine.skeleton.physics !== undefined) {
        this.spine.skeleton.updateWorldTransform(2);
      } else {
        this.spine.skeleton.updateWorldTransform(0);
      }
      this.spine.state.clearTracks();
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  _resolveSlot(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /** Largest font size at which `text` still fits the screen. Never scales up. */
  _fitFontSizeToWidth(text, baseStyle, baseFontSize, screenRatio = 0.85) {
    const spineScale = this.spine?.scale?.x || 1;
    const maxWidth = ((this.dimensions?.width || 800) * screenRatio) / spineScale;
    const metrics = PIXI.TextMetrics.measureText(
      text,
      new PIXI.TextStyle(scaleStyle(baseStyle, baseFontSize)),
    );
    if (metrics.width <= maxWidth || maxWidth <= 0) return baseFontSize;
    return Math.max(1, Math.round(baseFontSize * (maxWidth / metrics.width)));
  }

  _addSlotText(slotName, initialText, fontSize) {
    if (!slotName) return null;
    const container = new PIXI.Container();
    container.name = `free_spin_max_win_${slotName}`;
    const text = new PIXI.Text(
      initialText,
      new PIXI.TextStyle(scaleStyle(this._counterBaseStyle, fontSize)),
    );
    text.resolution = 2;
    text.anchor.set(0.5);
    container.addChild(text);
    this.spine.addSlotObject(slotName, container);
    return text;
  }

  initializeCounter() {
    this._counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    // WIN_TEXT_SCALE, so this popup and the base game's win celebration draw
    // the same spine at the same size — see fontStyles.
    this._counterBaseFontSize = Math.round(
      this._counterBaseStyle.fontSize * WIN_TEXT_SCALE,
    );
    this.counterText = this._addSlotText(
      this._resolveSlot(VALUE_SLOTS),
      "0",
      this._counterBaseFontSize,
    );
  }

  initializeMultiplier() {
    // ── From the xBet style's own size, not 0.6 of the counter ──
    //
    // 0.6 was inherited from the Pot Collection popup. It happened to land
    // within a pixel of the right answer, and it broke the moment the counter
    // moved: a fraction OF the counter follows every change to it, so raising
    // one raised the other and the two lines kept their ratio instead of their
    // sizes. The base game's win popup takes 48 from `winCelebrationXBet`, so
    // this does too, and both then scale by the same factor.
    this._multiplierBaseFontSize = Math.round(
      FONT_STYLES.winCelebrationXBet.fontSize * WIN_TEXT_SCALE,
    );
    const slotName = this._resolveSlot(MULTIPLIER_SLOTS);
    if (!slotName) {
      console.warn("[FreeSpinMaxWin] No multiplier slot:", MULTIPLIER_SLOTS);
      return;
    }
    // A rendered placeholder, not "". The slot is keyed by `in` alongside the
    // value, so an empty string means the line animates in as nothing at all —
    // which reads as a missing element rather than a pending one.
    this.multiplierText = this._addSlotText(
      slotName,
      this._multiplierString(0),
      this._multiplierBaseFontSize,
    );
  }

  initializeButton() {
    const built = attachTapToContinue({
      spine: this.spine,
      slotNames: BUTTON_SLOTS,
      label: this._buttonLabel(),
      name: "free_spin_max_win_continue_attached_to_slot",
      // No `tap_to_continue` here — Total Win's rule, and this popup is its
      // twin. The close sound is the CELEBRATION's (`win_animation_end`,
      // played in playClosingAnimation), because this is the end of the free
      // spins rather than a UI button. It also has to fire for the auto-close,
      // which never touches this handler.
      onClick: () => {
        this.handleUserClick();
      },
    });
    if (!built) return;
    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._setTapOffsetY = built.setOffsetY;
    this._revealMs = built.revealMs;
    this._fitTapButton(this.dimensions);
    this.applyButtonOffset();
  }

  /**
   * Lift the tap word off its bone, in SPINE UNITS.
   *
   * On the TEXT, because the spine rewrites the container's transform every
   * frame. Spine units rather than a multiple of symbolHeight: text.y is
   * spine-local, so the spine's own scale carries it, and the value can be
   * read straight off the bone geometry. The old form reduced to
   * `offset x SYMBOL_HEIGHT / deviceScale` anyway — the same constant, just
   * two indirections away from the numbers it had to match.
   */
  applyButtonOffset() {
    if (!this._setTapOffsetY) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this._setTapOffsetY(buttonYOffset);
  }

  _buttonLabel() {
    // TAP on every device — see FreeSpinOutroDialogController for why.
    return this.t("tap_to_continue").toUpperCase();
  }

  updateButtonText() {
    if (!this.buttonText) return;
    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  // ── Values ───────────────────────────────────────────────────────────────

  /**
   * The win and the stake it was made at.
   *
   * The counter is parked at ZERO, not at the total — startSequence runs it up
   * from there. Printing the total here is what made the number look like it
   * had always been on screen.
   */
  setTotalWinValue(totalWin, bet) {
    this._totalWinValue = totalWin || 0;
    this._betAmount = bet || 0;
    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    this.updateMultiplierDisplay(this._totalWinValue);
  }

  /**
   * Start the counter loop, once.
   *
   * Identical to FreeSpinTotalWinController.startCounterSound, and for the
   * same reason: `counter` is a LOOPING Howl, and Howler answers play() on a
   * running Howl with a SECOND sound id rather than a restart. Two stack and
   * the tick doubles. This screen was the one left un-migrated.
   */
  startCounterSound() {
    if (this.counterSoundPlaying) return;
    this.counterSoundPlaying = true;
    soundManager.play(SOUND_NAME.counter);
  }

  /**
   * Stop it, optionally marking the landing.
   *
   * `playEnd` uses the SAME `counter` sound played ONCE — there is no separate
   * end asset. This screen used to call `counter_end`, a name with no entry in
   * sounds.js and no file behind it, so the numbers landed in silence.
   *
   * NOT guarded on `counterSoundPlaying`: the Howl is shared with the other
   * win screens, so whichever controller stops it must always reach
   * soundManager. Guarding the stop is what left it looping for ever.
   */
  stopCounterSound(playEnd = false) {
    this.counterSoundPlaying = false;
    soundManager.stop(SOUND_NAME.counter);
    if (playEnd) soundManager.playOnce(SOUND_NAME.counter);
  }

  /**
   * Run the counter from `fromValue` to `toValue`.
   * Same shape as FreeSpinTotalWinController and the Pot Collection outro.
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    this.counterAnimationRef?.pause();
    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);
    this.startCounterSound();

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration,
      easing: "easeOutQuad",
      update: () => this.updateCounterDisplay(this.currentCounterValue),
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;
        this.stopCounterSound(true);
        this._counterFinished = true;
        this._revealAfterCounter();
        onComplete?.();
      },
    });
  }

  /** Skip the run-up and land on the total. What the FIRST tap does. */
  jumpCounterToValue(value) {
    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    this.stopCounterSound(true);
    this._counterFinished = true;
    this._revealAfterCounter();
  }

  /**
   * Reveal the xBet line and the tap word — once the counter has LANDED.
   *
   * Both slots are keyed by the spine's `in`, so they would otherwise appear
   * while the count is still running: the multiplier showing a figure that is
   * not final, and the prompt asking for a tap mid-count.
   *
   * Called from BOTH ways the counter can end — its own completion, and the
   * first tap jumping it — and latched, so whichever arrives second finds
   * nothing to do.
   *
   * Never faded onto `in`: that clip KEYS these slots, so a fade over it is
   * fought every frame and again at the handover to `idle`. While the intro
   * owns the track the reveal is held as a REQUEST and replayed the moment the
   * idle takes over — see the inIdleTimeout in startSequence.
   */
  _revealAfterCounter() {
    if (this._revealedAfterCounter) return;
    if (this._closing || !this.isPlaying) return;

    if (this.spine?.state?.tracks?.[0]?.animation?.name !== "idle") {
      this._revealPending = true;
      return;
    }

    this._revealPending = false;
    this._revealedAfterCounter = true;

    for (const text of [this.multiplierText, this.buttonText]) {
      if (!text) continue;
      // Idempotent: one fade per text, ever. Two overlapping tweens on the
      // same alpha read as a stutter rather than a fade.
      anime.remove(text);
      text.alpha = 0;
      anime({
        targets: text,
        alpha: 1,
        duration: 400,
        easing: "easeOutCubic",
      });
    }
  }

  updateCounterDisplay(value) {
    if (!this.counterText) return;
    const text = formatToCustomLocale(Math.floor(value));
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._counterBaseFontSize,
    );
    this.counterText.text = text;
    this.counterText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
  }

  /** "12.5x BET" — one formatter, so the placeholder cannot drift from the real value. */
  _multiplierString(multiplier) {
    const bet = this.t("bet").toUpperCase();
    return `${multiplier.toLocaleString("en-US")}x ${bet}`;
  }

  updateMultiplierDisplay(winAmount) {
    if (!this.multiplierText) return;

    // Falls back to the placeholder rather than bailing out. Bailing left the
    // last string on screen — and at startup that was the empty one, so the
    // line simply never appeared.
    const multiplier =
      this._betAmount > 0
        ? parseFloat((winAmount / this._betAmount).toFixed(2))
        : 0;

    const text = this._multiplierString(multiplier);
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._multiplierBaseFontSize,
    );
    this.multiplierText.text = text;
    this.multiplierText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
  }

  getSpineAnimationDuration(name) {
    const anim = this.spine?.skeleton?.data?.animations?.find(
      (a) => a.name === name,
    );
    return anim?.duration ? Math.round(anim.duration * 1000) : 1000;
  }

  _clearTimeouts() {
    for (const key of [
      "skipProtectionTimeout",
      "debounceTimeout",
      "inIdleTimeout",
    ]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  startSequence() {
    if (this.isPlaying || !this.spine?.state) return;

    this.isPlaying = true;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this._clearTimeouts();

    this.overlayRef?.current?.fadeInBackground();

    // ── THE MUSIC IS NOT DUCKED. Total Win's rule, and this is its twin. ──
    //
    // The two popups are the same dialog for different endings, so they close
    // the round the same way. Max Win used to duck the feature track and reach
    // for a `max_win_loop` to put in its place — Pot Collection's shape, carried
    // over. There is no such file in this game and there is no total_win_loop
    // either, so what actually happened was: the room went quiet, the one-shot
    // played, and the popup then sat in SILENCE for up to 30 seconds.
    //
    // The music is handed over exactly ONCE and the TRANSITION owns it: the
    // wipe out of the room fades the feature track down as it starts and fades
    // the base track in when it completes. So the player hears one continuous
    // piece from the last free spin into the base game, with the announcement
    // over the top of it. Vladimir, 2026-09-23: *"it will work just like total
    // win... we dont have that total win loop music for this game."*
    soundManager.play(SOUND_NAME.max_win);
    // ...and the celebration's loop underneath it, until this popup closes.
    // Both outro popups do exactly this — see outroWinMusic.
    startOutroWinMusic();

    this.applyScaleAndCenter();

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.state.apply(this.spine.skeleton);
    this.spine.skeleton.setToSetupPose();
    // setToSetupPose reverts the skin's slot state, so re-apply after it.
    this.applySkin();

    this.skipProtectionTimeout = new TickerTimeout(() => {
      // Only unlocks INPUT. `isWaitingForClick` — the right to CLOSE — is
      // raised once the total is on screen, in _startWaitingForClick. A tap
      // before that jumps the counter instead of dismissing the popup.
      if (this.isPlaying) this.canSkipAnimation = true;
    }, this._revealMs?.("in") ?? SKIP_PROTECTION_MS);

    // Played while STILL HIDDEN, shown on the NEXT frame.
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "in", false);
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) this.spine.alpha = 1;
    });

    this.inIdleTimeout = new TickerTimeout(() => {
      // ── Not over the CLOSE ──
      //
      // Two taps in quick succession finish the counter and then dismiss, and
      // both can land inside `in`, which outlasts them. Armed for the whole of
      // `in`, this would otherwise fire afterwards and set a LOOPING idle on
      // top of `out` — the popup drawing itself again while its close listener
      // waits for a clip that never comes.
      if (!this.isPlaying || this._closing) return;
      if (this.spine) this.spine.autoUpdate = true;
      this.spine.state.setAnimation(0, "idle", true);
      // The idle owns the slots now — release a reveal the intro held back.
      if (this._revealPending) this._revealAfterCounter();
    }, this.getSpineAnimationDuration("in"));

    // The counter starts with `in` and runs alongside it, so the number is
    // already climbing as the art arrives.
    this._counterFinished = false;
    // Re-armed per showing, or a second max win would open with both texts
    // already up.
    this._revealedAfterCounter = false;
    this._revealPending = false;
    this._closing = false;

    // ── And HIDDEN again, which the latches above do not do ──
    //
    // Re-arming the latches only means the reveal will run AGAIN; it says
    // nothing about what the texts currently show. `anime.remove` first, or a
    // fade still in flight from the previous showing animates them straight
    // back to 1 one frame after this sets them to 0.
    for (const text of [this.multiplierText, this.buttonText]) {
      if (!text) continue;
      anime.remove(text);
      text.alpha = 0;
    }

    this.startCounterAnimation(
      0,
      this._totalWinValue,
      COUNTER_DURATION_MS,
      () => this._startWaitingForClick(),
    );
  }

  /**
   * The total is on screen; the popup may now be dismissed.
   *
   * GDD §16.4 — Max Win has NO auto-close, so unlike the total win popup this
   * only opens the gate and arms no timer.
   */
  _startWaitingForClick() {
    if (this.isPlaying) this.isWaitingForClick = true;
  }

  /**
   * TWO STAGES. The first tap finishes the counter, the second closes.
   *
   * Dismissing mid-count would rob the player of the number they are watching
   * arrive — and on a capped round that number is the whole point — so a tap
   * during the run-up jumps it to the total instead.
   */
  handleUserClick() {
    if (!this.isPlaying || !this.spine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, CLICK_DEBOUNCE_MS);

    if (!this._counterFinished) {
      this.jumpCounterToValue(this._totalWinValue);
      this._startWaitingForClick();
      return true;
    }

    if (!this.isWaitingForClick) return false;
    this.playClosingAnimation();
    return true;
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.spine?.state) return;
    this.isWaitingForClick = false;

    // Said once, read by everything the intro left armed — see the
    // inIdleTimeout and _revealAfterCounter.
    this._closing = true;
    this.inIdleTimeout?.clear();
    this.inIdleTimeout = null;

    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.stopCounterSound();

    // ── This screen closes with the CELEBRATION's sound ──
    //
    // `win_animation_end`, not `tap_to_continue` — the same choice Total Win
    // makes, for the same reason: this is the end of the free-spins
    // celebration, not a UI button. HERE and not on the button, because this
    // method IS the out clip, it is guarded by `isWaitingForClick` so it runs
    // exactly once, and the auto-close reaches it too — so the sound follows
    // the animation rather than the gesture.
    // soundManager.play(SOUND_NAME.win_animation_end);
    // ── The DRUM END, as Total Win and the Royal (2026-11-02) ──
    // win_animation_end is Mad Labs' shot.
    soundManager.play(SOUND_NAME.anticipation_end);
    // The loop leaves with the art; the wipe brings the base track in.
    stopOutroWinMusic();

    this.spine.state.clearListeners();
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "out", false);
    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Nothing to restore — the feature track was never ducked. Fading it
          // in here would raise a track that is already at full and the wipe
          // would fade it out again a beat later: two fades over one handover.
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Close, then hand over to the wipe back — exactly as the TOTAL WIN popup
   * does, because this popup REPLACES it.
   *
   * That flow now exists. Max Win is the closing popup of a capped round, so
   * the way out cannot be left to a round that has already finished spinning:
   * nothing else would ever start the transition and the player would sit on a
   * dismissed popup in the bonus room for good.
   *
   * Ordering copied from FreeSpinTotalWinController, and it matters: the
   * dispatch that closes THIS dialog is first and synchronous, or the flag
   * stays set across the fade and the popup can re-trigger. Pot Collection's
   * outro carries the same note. The wipe's own midpoint tears the round down
   * under full cover, so nothing about leaving is decided here.
   */
  finishAnimation() {
    if (!this.spine || !this.initialized) return;

    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.stopCounterSound();

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.spine.alpha = 0;
    if (this.spine) this.spine.autoUpdate = false;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    // FIRST, and synchronous — see above.
    // this.dispatch?.(setShowFreeSpinMaxWin(false));
    this.dispatch?.(this.closeAction(false));
    // Then the way out.
    // this.dispatch?.(setShowTransitionDialog(true));
    // The dev panel's preview (no free-spins room up) just closes — no wipe
    // out of a room that is not there. Live play is unchanged (2026-09-30).
    // const devPreview =
    //   FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && !store.getState().freeSpins?.active;
    // Hold & Win wipes out only when its room is really up.
    const devPreview = this.isHoldAndWin
      ? !store.getState().holdAndWin?.active
      : FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && !store.getState().freeSpins?.active;
    if (!devPreview) this.dispatch?.(setShowTransitionDialog(true));
    this.overlayRef?.current?.fadeOutBackground();
  }

  cleanup() {
    this.counterAnimationRef?.pause();
    this.counterAnimationRef = null;
    this.stopCounterSound();
    // The fades hold their own reference to these texts and would otherwise
    // keep ticking against destroyed objects.
    for (const text of [this.multiplierText, this.buttonText]) {
      if (text) anime.remove(text);
    }

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.spine.removeSlotObjects) this.spine.removeSlotObjects();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    this.counterText = null;
    this.multiplierText = null;
    this.buttonContainer = null;
    this.buttonText = null;
    this._fitTapButton = null;
    this.initialized = false;
  }
}

export default FreeSpinMaxWinController;
